/**
 * The parent side of the preview frame protocol (browser): drives a sandboxed frame that runs the frame host
 * (host.ts). Used by the editor's Preview panel and by the framed harness page (page/framed.html), which the api's
 * save-time check and the e2e tests run headless.
 *
 * Only messages whose source is this frame's window are accepted. The frame has an opaque origin
 * (sandbox="allow-scripts" without allow-same-origin), so messages to it use "*"; they carry the package's own
 * files and the calls, never anything secret.
 */
import type { CallRecord, Inspection, LoadedPackage } from '../page/types.ts';
import { header, isProtocolMessage, type FromFrame, type ToFrame } from './protocol.ts';

export type { CallRecord, Inspection, LoadedPackage } from '../page/types.ts';

export interface PreviewFrameEvents {
  /** A console error, uncaught error or unhandled rejection in the frame. */
  onConsole?: (text: string, level: 'error' | 'warn') => void;
  /** A call started (record without result) or settled. */
  onRecord?: (record: CallRecord) => void;
}

/** Package files: path inside the package (no leading `<id>/`) → bytes. */
export type PackageFiles = Map<string, Uint8Array> | Record<string, Uint8Array | ArrayBuffer>;

export class PreviewFrame {
  readonly iframe: HTMLIFrameElement;
  readonly #events: PreviewFrameEvents;
  readonly #records = new Map<number, CallRecord>();
  readonly #waiting = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  readonly #settled = new Map<number, { promise: Promise<CallRecord>; resolve: (r: CallRecord) => void }>();
  readonly #errors: string[] = [];
  #queue: Array<{ id: number; method: string; params?: unknown }> = [];
  #nextId = 1;
  #ready: Promise<void>;
  #markReady!: () => void;
  #listener: (e: MessageEvent) => void;

  constructor(iframe: HTMLIFrameElement, events: PreviewFrameEvents = {}) {
    this.iframe = iframe;
    this.#events = events;
    this.#ready = new Promise((r) => (this.#markReady = r));
    this.#listener = (e) => this.#onMessage(e);
    window.addEventListener('message', this.#listener);
    // The host announces itself when it starts; if it already has, this asks again.
    try {
      iframe.contentWindow?.postMessage({ ...header, type: 'hello' }, '*');
    } catch {
      // not attached yet: the host's own "ready" arrives later
    }
  }

  /** Resolves once the frame host has started (its "ready" message). */
  ready(): Promise<void> {
    return this.#ready;
  }

  dispose(): void {
    window.removeEventListener('message', this.#listener);
    for (const w of this.#waiting.values()) w.reject(new Error('preview frame closed'));
    this.#waiting.clear();
  }

  #onMessage(e: MessageEvent): void {
    if (e.source !== this.iframe.contentWindow || !isProtocolMessage(e.data)) return;
    const m = e.data as FromFrame;
    switch (m.type) {
      case 'ready':
        this.#markReady();
        return;
      case 'reply': {
        const w = this.#waiting.get(m.id);
        this.#waiting.delete(m.id);
        if (m.ok) w?.resolve(m.value);
        else w?.reject(new Error(m.error));
        return;
      }
      case 'call-start':
      case 'call-end': {
        this.#records.set(m.record.id, m.record);
        this.#events.onRecord?.(m.record);
        if (m.type === 'call-end') this.#settled.get(m.record.id)?.resolve(m.record);
        return;
      }
      case 'console':
        this.#errors.push(m.text);
        this.#events.onConsole?.(m.text, m.level);
        return;
    }
  }

  #send(msg: ToFrame, transfer: Transferable[] = []): void {
    const w = this.iframe.contentWindow;
    if (!w) throw new Error('preview frame is not attached');
    w.postMessage(msg, '*', transfer);
  }

  async #request<T>(build: (id: number) => ToFrame, transfer: Transferable[] = []): Promise<T> {
    await this.#ready;
    const id = this.#nextId++;
    const p = new Promise<T>((resolve, reject) => this.#waiting.set(id, { resolve: resolve as (v: unknown) => void, reject }));
    this.#send(build(id), transfer);
    return p;
  }

  /**
   * Load a package from its files (always blob: URLs + v_assetUrls inside the frame). `size`: the graphic's element
   * size (default the manifest's first resolution), e.g. another layout's (ADR 0037).
   */
  loadFiles(files: PackageFiles, manifestPath: string, size?: { width: number; height: number }): Promise<LoadedPackage> {
    const out: Record<string, ArrayBuffer> = {};
    const transfer: ArrayBuffer[] = [];
    const entries = files instanceof Map ? [...files.entries()] : Object.entries(files);
    for (const [path, bytes] of entries) {
      // Copy, so the caller keeps its bytes; the copy is transferred, not cloned again.
      const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
      const buf = u8.slice().buffer as ArrayBuffer;
      out[path] = buf;
      transfer.push(buf);
    }
    this.#records.clear();
    return this.#request((id) => ({ ...header, type: 'load-files', id, files: out, manifestPath, ...(size ? { size } : {}) }), transfer);
  }

  /** Load a package by URL (the frame fetches it; the server must allow CORS from the opaque origin). */
  loadUrl(manifestUrl: string, opts: { blobUrls?: boolean } = {}): Promise<LoadedPackage> {
    this.#records.clear();
    return this.#request((id) => ({ ...header, type: 'load-url', id, manifestUrl, blobUrls: opts.blobUrls ?? false }));
  }

  /**
   * Start a call without waiting; returns its id. Calls started in the same task go to the frame in one message
   * and start there back to back, without awaiting in between.
   */
  start(method: string, params?: unknown): number {
    const id = this.#nextId++;
    let resolve!: (r: CallRecord) => void;
    const promise = new Promise<CallRecord>((r) => (resolve = r));
    this.#settled.set(id, { promise, resolve });
    this.#queue.push(params === undefined ? { id, method } : { id, method, params });
    if (this.#queue.length === 1) {
      queueMicrotask(() => {
        const calls = this.#queue;
        this.#queue = [];
        void this.#ready.then(() => this.#send({ ...header, type: 'calls', calls }));
      });
    }
    return id;
  }

  settle(id: number): Promise<CallRecord> {
    const s = this.#settled.get(id);
    if (!s) return Promise.reject(new Error(`no call ${id}`));
    return s.promise;
  }

  call(method: string, params?: unknown): Promise<CallRecord> {
    return this.settle(this.start(method, params));
  }

  records(): CallRecord[] {
    return [...this.#records.values()].sort((a, b) => a.id - b.id);
  }

  errors(): string[] {
    return [...this.#errors];
  }

  inspect(): Promise<Inspection> {
    return this.#request((id) => ({ ...header, type: 'inspect', id }));
  }

  async remove(): Promise<void> {
    await this.#request((id) => ({ ...header, type: 'remove', id }));
  }
}
